import axios from 'axios';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import { IDesign } from '../interfaces/Order';

export interface DownloadProgress {
  current: number;
  total: number;
  fileName: string;
  status: 'downloading' | 'processing' | 'complete' | 'error';
}

export interface VariantSummary {
  color: string;
  size: string;
  productName: string;
  quantity: number;
  designs: number;
}

export interface DesignWithOrderContext extends IDesign {
  orderId?: string;
  orderIndex?: number;
  // Variant information for summary
  productTemplateName?: string;
  productColor?: string;
  productSize?: string;
  quantity?: number;
  // Placement information for front/back designs
  placementIndex?: number;
  side?: string;
  printArea?: string;
  // Copy number for quantity-based multiplication
  copyNumber?: number;
}

export interface OrderFilesContext {
  orderId: string;
  designs: DesignWithOrderContext[];
  trackingLabelUrl?: string;
  variantSummary?: VariantSummary[];
}

export interface DownloadOptions {
  onProgress?: (progress: DownloadProgress) => void;
  onError?: (error: Error, fileName: string) => void;
  maxFileSize?: number; // in MB
  timeout?: number; // in ms
}

class DesignDownloadService {
  private static readonly DEFAULT_MAX_FILE_SIZE = 50; // 50MB per file
  private static readonly DEFAULT_TIMEOUT = 30000; // 30 seconds
  private static readonly MAX_CONCURRENT_DOWNLOADS = 5;

  /**
   * Downloads order files organized by folders and packages them into a ZIP file
   */
  public static async downloadOrderFilesAsZip(
    orderFiles: OrderFilesContext[],
    zipFileName: string = 'orders.zip',
    options: DownloadOptions = {}
  ): Promise<void> {
    const {
      onProgress,
      onError,
      maxFileSize = this.DEFAULT_MAX_FILE_SIZE,
      timeout = this.DEFAULT_TIMEOUT,
    } = options;

    if (!orderFiles || orderFiles.length === 0) {
      throw new Error('Không có đơn hàng nào để tải xuống');
    }

    // Calculate total files (designs + tracking labels)
    const totalFiles = orderFiles.reduce((total, order) => {
      return total + order.designs.length + (order.trackingLabelUrl ? 1 : 0);
    }, 0);

    if (totalFiles === 0) {
      throw new Error('Không có tệp nào để tải xuống');
    }

    const zip = new JSZip();
    let completedDownloads = 0;

    // Process each order
    for (const order of orderFiles) {
      const folderName = order.orderId;
      const orderFolder = zip.folder(folderName);

      if (!orderFolder) {
        console.error(`Failed to create folder for order ${order.orderId}`);
        continue;
      }

      // Download designs for this order
      for (
        let i = 0;
        i < order.designs.length;
        i += this.MAX_CONCURRENT_DOWNLOADS
      ) {
        const batch = order.designs.slice(i, i + this.MAX_CONCURRENT_DOWNLOADS);

        const batchPromises = batch.map(async (design) => {
          try {
            const displayName = `${order.orderId}/${this.generateDisplayName(
              design
            )}`;
            onProgress?.({
              current: completedDownloads,
              total: totalFiles,
              fileName: displayName,
              status: 'downloading',
            });

            const fileBlob = await this.downloadDesignFile(design, {
              maxFileSize,
              timeout,
            });

            const fileName = this.generateFileName(design);
            orderFolder.file(fileName, fileBlob);

            completedDownloads++;
            onProgress?.({
              current: completedDownloads,
              total: totalFiles,
              fileName: displayName,
              status: 'complete',
            });
          } catch (error) {
            const displayName = `${order.orderId}/${this.generateDisplayName(
              design
            )}`;
            console.error(`Failed to download design ${displayName}:`, error);
            onError?.(error as Error, displayName);

            // Continue with other downloads even if one fails
            completedDownloads++;
            onProgress?.({
              current: completedDownloads,
              total: totalFiles,
              fileName: displayName,
              status: 'error',
            });
          }
        });

        await Promise.all(batchPromises);
      }

      // Download tracking label if available
      if (order.trackingLabelUrl) {
        try {
          const displayName = `${order.orderId}/tracking-label`;
          onProgress?.({
            current: completedDownloads,
            total: totalFiles,
            fileName: displayName,
            status: 'downloading',
          });

          const labelBlob = await this.downloadFileFromUrl(
            order.trackingLabelUrl,
            {
              maxFileSize,
              timeout,
            }
          );

          // Try to determine file extension from URL or use .pdf as default
          const extension =
            this.getFileExtensionFromUrl(order.trackingLabelUrl) || '.pdf';
          orderFolder.file(`tracking-label${extension}`, labelBlob);

          completedDownloads++;
          onProgress?.({
            current: completedDownloads,
            total: totalFiles,
            fileName: displayName,
            status: 'complete',
          });
        } catch (error) {
          const displayName = `${order.orderId}/tracking-label`;
          console.error(
            `Failed to download tracking label ${displayName}:`,
            error
          );
          onError?.(error as Error, displayName);

          completedDownloads++;
          onProgress?.({
            current: completedDownloads,
            total: totalFiles,
            fileName: displayName,
            status: 'error',
          });
        }
      }
    }

    // Generate and download the ZIP file
    onProgress?.({
      current: totalFiles,
      total: totalFiles,
      fileName: zipFileName,
      status: 'processing',
    });

    try {
      const zipBlob = await zip.generateAsync({
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 6 },
      });

      saveAs(zipBlob, zipFileName);

      onProgress?.({
        current: totalFiles,
        total: totalFiles,
        fileName: zipFileName,
        status: 'complete',
      });
    } catch (error) {
      throw new Error(`Không thể tạo tệp ZIP: ${error}`);
    }
  }

  /**
   * Downloads multiple designs and packages them into a ZIP file (legacy method)
   */
  public static async downloadDesignsAsZip(
    designs: DesignWithOrderContext[],
    zipFileName: string = 'designs.zip',
    options: DownloadOptions = {}
  ): Promise<void> {
    const {
      onProgress,
      onError,
      maxFileSize = this.DEFAULT_MAX_FILE_SIZE,
      timeout = this.DEFAULT_TIMEOUT,
    } = options;

    if (!designs || designs.length === 0) {
      throw new Error('Không có thiết kế nào để tải xuống');
    }

    const zip = new JSZip();
    let completedDownloads = 0;

    // Process downloads in batches to avoid overwhelming the browser
    for (let i = 0; i < designs.length; i += this.MAX_CONCURRENT_DOWNLOADS) {
      const batch = designs.slice(i, i + this.MAX_CONCURRENT_DOWNLOADS);

      const batchPromises = batch.map(async (design) => {
        try {
          const displayName = this.generateDisplayName(design);
          onProgress?.({
            current: completedDownloads,
            total: designs.length,
            fileName: displayName,
            status: 'downloading',
          });

          const fileBlob = await this.downloadDesignFile(design, {
            maxFileSize,
            timeout,
          });

          const fileName = this.generateFileName(design);
          zip.file(fileName, fileBlob);

          completedDownloads++;
          onProgress?.({
            current: completedDownloads,
            total: designs.length,
            fileName: displayName,
            status: 'complete',
          });
        } catch (error) {
          const displayName = this.generateDisplayName(design);
          console.error(`Failed to download design ${displayName}:`, error);
          onError?.(error as Error, displayName);

          // Continue with other downloads even if one fails
          completedDownloads++;
          onProgress?.({
            current: completedDownloads,
            total: designs.length,
            fileName: displayName,
            status: 'error',
          });
        }
      });

      // Wait for current batch to complete before starting next batch
      await Promise.all(batchPromises);
    }

    // Generate and download the ZIP file
    onProgress?.({
      current: designs.length,
      total: designs.length,
      fileName: zipFileName,
      status: 'processing',
    });

    try {
      const zipBlob = await zip.generateAsync({
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 6 },
      });

      saveAs(zipBlob, zipFileName);

      onProgress?.({
        current: designs.length,
        total: designs.length,
        fileName: zipFileName,
        status: 'complete',
      });
    } catch (error) {
      throw new Error(`Không thể tạo tệp ZIP: ${error}`);
    }
  }

  /**
   * Downloads multiple tracking labels and packages them into a ZIP file
   * All labels are placed in a single folder (not per-order folders)
   */
  public static async downloadTrackingLabelsAsZip(
    trackingLabels: Array<{ url: string; orderId: string }>,
    zipFileName: string = 'trackings.zip',
    options: DownloadOptions = {}
  ): Promise<void> {
    const {
      onProgress,
      onError,
      maxFileSize = this.DEFAULT_MAX_FILE_SIZE,
      timeout = this.DEFAULT_TIMEOUT,
    } = options;

    if (!trackingLabels || trackingLabels.length === 0) {
      throw new Error('Không có nhãn vận chuyển nào để tải xuống');
    }

    const zip = new JSZip();
    let completedDownloads = 0;

    // Process downloads in batches to avoid overwhelming the browser
    for (
      let i = 0;
      i < trackingLabels.length;
      i += this.MAX_CONCURRENT_DOWNLOADS
    ) {
      const batch = trackingLabels.slice(i, i + this.MAX_CONCURRENT_DOWNLOADS);

      const batchPromises = batch.map(async (trackingLabel) => {
        try {
          const displayName = `tracking-${trackingLabel.orderId}`;
          onProgress?.({
            current: completedDownloads,
            total: trackingLabels.length,
            fileName: displayName,
            status: 'downloading',
          });

          const labelBlob = await this.downloadFileFromUrl(trackingLabel.url, {
            maxFileSize,
            timeout,
          });

          // Try to determine file extension from URL or use .pdf as default
          const extension =
            this.getFileExtensionFromUrl(trackingLabel.url) || '.pdf';
          const fileName = `${trackingLabel.orderId}${extension}`;
          zip.file(fileName, labelBlob);

          completedDownloads++;
          onProgress?.({
            current: completedDownloads,
            total: trackingLabels.length,
            fileName: displayName,
            status: 'complete',
          });
        } catch (error) {
          const displayName = `tracking-${trackingLabel.orderId}`;
          console.error(
            `Failed to download tracking label ${displayName}:`,
            error
          );
          onError?.(error as Error, displayName);

          // Continue with other downloads even if one fails
          completedDownloads++;
          onProgress?.({
            current: completedDownloads,
            total: trackingLabels.length,
            fileName: displayName,
            status: 'error',
          });
        }
      });

      // Wait for current batch to complete before starting next batch
      await Promise.all(batchPromises);
    }

    // Generate and download the ZIP file
    onProgress?.({
      current: trackingLabels.length,
      total: trackingLabels.length,
      fileName: zipFileName,
      status: 'processing',
    });

    try {
      const zipBlob = await zip.generateAsync({
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 6 },
      });

      saveAs(zipBlob, zipFileName);

      onProgress?.({
        current: trackingLabels.length,
        total: trackingLabels.length,
        fileName: zipFileName,
        status: 'complete',
      });
    } catch (error) {
      throw new Error(`Không thể tạo tệp ZIP: ${error}`);
    }
  }

  /**
   * Downloads a single design file and returns it as a Blob
   */
  private static async downloadDesignFile(
    design: DesignWithOrderContext,
    options: { maxFileSize: number; timeout: number }
  ): Promise<Blob> {
    const url = design.originalFileUrl || design.designUrl;

    if (!url) {
      throw new Error(
        `Không có URL tải xuống cho thiết kế ${design.sku || design.id}`
      );
    }

    return this.downloadFileFromUrl(url, options);
  }

  /**
   * Downloads a file from URL and returns it as a Blob using axios
   */
  private static async downloadFileFromUrl(
    url: string,
    options: { maxFileSize: number; timeout: number }
  ): Promise<Blob> {
    try {
      // First attempt: Try with axios
      return await this.downloadWithAxios(url, options);
    } catch (axiosError: any) {
      console.warn(
        'Axios download failed, trying fallback method:',
        axiosError.message
      );

      // Fallback: Try with fetch API (sometimes works better for CORS)
      try {
        return await this.downloadWithFetch(url, options);
      } catch (fetchError: any) {
        console.warn(
          'Fetch download failed, trying XMLHttpRequest:',
          fetchError.message
        );

        // Last resort: Try with XMLHttpRequest with different settings
        try {
          return await this.downloadWithXHR(url, options);
        } catch (xhrError: any) {
          // All methods failed, throw the most descriptive error
          throw new Error(
            `Không thể tải xuống file: ${axiosError.message}. CORS có thể chưa được cấu hình đúng.`
          );
        }
      }
    }
  }

  private static async downloadWithAxios(
    url: string,
    options: { maxFileSize: number; timeout: number }
  ): Promise<Blob> {
    const response = await axios.get(url, {
      responseType: 'blob',
      timeout: options.timeout,
      headers: {
        Accept: '*/*',
      },
      withCredentials: false,
      maxRedirects: 5,
      validateStatus: (status) => status >= 200 && status < 300,
    });

    const blob = response.data;
    this.validateFileSize(blob, options.maxFileSize);
    return blob;
  }

  private static async downloadWithFetch(
    url: string,
    options: { maxFileSize: number; timeout: number }
  ): Promise<Blob> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), options.timeout);

    try {
      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors', // Try CORS mode first
        signal: controller.signal,
        headers: {
          Accept: '*/*',
        },
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const blob = await response.blob();
      this.validateFileSize(blob, options.maxFileSize);
      return blob;
    } catch (error: any) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Hết thời gian tải xuống');
      }
      throw error;
    }
  }

  private static async downloadWithXHR(
    url: string,
    options: { maxFileSize: number; timeout: number }
  ): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.timeout = options.timeout;
      xhr.ontimeout = () => reject(new Error('Hết thời gian tải xuống'));

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const blob = xhr.response;
          try {
            this.validateFileSize(blob, options.maxFileSize);
            resolve(blob);
          } catch (error) {
            reject(error);
          }
        } else {
          reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`));
        }
      };

      xhr.onerror = () => reject(new Error('Lỗi mạng'));
      xhr.onabort = () => reject(new Error('Tải xuống bị hủy'));

      xhr.open('GET', url);
      xhr.responseType = 'blob';
      xhr.send();
    });
  }

  private static validateFileSize(blob: Blob, maxFileSize: number): void {
    const fileSizeMB = blob.size / (1024 * 1024);
    if (fileSizeMB > maxFileSize) {
      throw new Error(
        `Tệp quá lớn: ${fileSizeMB.toFixed(2)}MB (tối đa: ${maxFileSize}MB)`
      );
    }
  }

  /**
   * Generates a filename for a design using {orderId}-{index} format with placement info
   */
  private static generateFileName(design: DesignWithOrderContext): string {
    // Try to get file extension from URL
    const url = design.originalFileUrl || design.designUrl;
    let extension = '.jpg'; // default extension

    if (url) {
      const urlParts = url.split('.');
      if (urlParts.length > 1) {
        const lastPart = urlParts[urlParts.length - 1].split('?')[0]; // Remove query params
        if (lastPart.match(/^(jpg|jpeg|png|gif|svg|pdf|ai|psd)$/i)) {
          extension = `.${lastPart.toLowerCase()}`;
        }
      }
    }

    // Use orderId-index format with placement info and copy number if available
    if (design.orderId && design.orderIndex !== undefined) {
      let baseFilename = `${design.orderId}-${design.orderIndex}`;

      // Add placement info for front/back designs
      if (design.placementIndex !== undefined && design.side) {
        baseFilename += `-${design.side}`;
        // If there are multiple placements on the same side, add placement index
        if (design.placementIndex > 1) {
          baseFilename += `-${design.placementIndex}`;
        }
      }

      // Add copy number for quantity-based multiplication
      if (design.copyNumber !== undefined) {
        baseFilename += `-copy${design.copyNumber}`;
      }

      return `${baseFilename}${extension}`;
    }

    // Fallback to original format
    const sku = design.sku || design.id;
    const timestamp = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
    const sanitizedSku = sku.replace(/[^a-zA-Z0-9-_]/g, '_');
    return `${sanitizedSku}_${timestamp}${extension}`;
  }

  /**
   * Generates a display name for progress tracking
   */
  private static generateDisplayName(design: DesignWithOrderContext): string {
    if (design.orderId && design.orderIndex !== undefined) {
      let displayName = `${design.orderId}-${design.orderIndex}`;

      // Add placement info for clarity in progress tracking
      if (design.side) {
        displayName += ` (${design.side})`;
      }

      // Add copy number for quantity tracking
      if (design.copyNumber !== undefined) {
        displayName += ` copy ${design.copyNumber}`;
      }

      return displayName;
    }
    return design.sku || design.id;
  }

  /**
   * Gets file extension from URL
   */
  private static getFileExtensionFromUrl(url: string): string | null {
    try {
      const urlParts = url.split('.');
      if (urlParts.length > 1) {
        const lastPart = urlParts[urlParts.length - 1].split('?')[0]; // Remove query params
        if (lastPart.match(/^(jpg|jpeg|png|gif|svg|pdf|ai|psd|doc|docx)$/i)) {
          return `.${lastPart.toLowerCase()}`;
        }
      }
    } catch (error) {
      console.warn('Failed to extract file extension from URL:', url);
    }
    return null;
  }

  /**
   * Validates if designs can be downloaded
   */
  public static validateDesigns(designs: IDesign[]): {
    valid: IDesign[];
    invalid: IDesign[];
  } {
    const valid: IDesign[] = [];
    const invalid: IDesign[] = [];

    designs.forEach((design) => {
      const hasUrl = design.originalFileUrl || design.designUrl;
      if (hasUrl) {
        valid.push(design);
      } else {
        invalid.push(design);
      }
    });

    return { valid, invalid };
  }

  /**
   * Estimates total download size (rough estimate)
   */
  public static estimateDownloadSize(designs: IDesign[]): string {
    // Rough estimate: assume average design file is 2MB
    const estimatedSizeMB = designs.length * 2;

    if (estimatedSizeMB < 1024) {
      return `~${estimatedSizeMB}MB`;
    } else {
      return `~${(estimatedSizeMB / 1024).toFixed(1)}GB`;
    }
  }

  /**
   * Generates variant summary from order files
   */
  public static generateVariantSummary(
    orderFiles: OrderFilesContext[]
  ): VariantSummary[] {
    const variantMap = new Map<string, VariantSummary>();

    orderFiles.forEach((order) => {
      order.designs.forEach((design) => {
        if (
          design.productColor &&
          design.productSize &&
          design.productTemplateName
        ) {
          const key = `${design.productColor}-${design.productSize}-${design.productTemplateName}`;

          if (variantMap.has(key)) {
            const existing = variantMap.get(key)!;
            // For placement-based designs, each placement counts as a separate design
            // but the quantity should remain the same (since it's the same order item)
            existing.designs += 1;
            // Only add quantity if this is a new order item, not just a new placement
            if (!design.placementIndex || design.placementIndex === 1) {
              existing.quantity += design.quantity || 1;
            }
          } else {
            variantMap.set(key, {
              color: design.productColor,
              size: design.productSize,
              productName: design.productTemplateName,
              quantity: design.quantity || 1,
              designs: 1,
            });
          }
        }
      });
    });

    // Convert map to array and sort by color, then size
    return Array.from(variantMap.values()).sort((a, b) => {
      const colorCompare = a.color.localeCompare(b.color);
      if (colorCompare !== 0) return colorCompare;

      // Sort sizes: S, M, L, XL, XXL, XXXL
      const sizeOrder = ['S', 'M', 'L', 'XL', 'XXL', 'XXXL'];
      const aIndex = sizeOrder.indexOf(a.size);
      const bIndex = sizeOrder.indexOf(b.size);
      return aIndex - bIndex;
    });
  }
}

export default DesignDownloadService;
